import React, { useEffect, useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import './ImageCarousel.css';

interface ImageCarouselProps {
  images: string[];
  alt?: string;
}

interface TransitionState {
  index: number;
  direction: 1 | -1; // 1 = next (enters from the right), -1 = previous (enters from the left)
}

const SWIPE_FRACTION = 0.25; // fraction of the stage width needed to trigger a swipe
const FLICK_MIN_DX = 24; // minimum distance for a "flick"
const FLICK_MAX_MS = 250; // maximum duration for a "flick"
const SNAP_MS = 300; // snap-back animation length

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, alt = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transition, setTransition] = useState<TransitionState | null>(null);
  const [overlayReady, setOverlayReady] = useState(false);
  const [dragDx, setDragDx] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; time: number; pointerId: number } | null>(null);
  const dragDxRef = useRef(0);
  const snapTimerRef = useRef<number | null>(null);

  const count = images.length;

  // Clean up any pending snap-back timer
  useEffect(() => {
    return () => {
      if (snapTimerRef.current !== null) {
        window.clearTimeout(snapTimerRef.current);
      }
    };
  }, []);

  // Preload the target image while the previous one is still visible,
  // then reveal + animate it once it has finished loading.
  useEffect(() => {
    if (!transition) return;
    let cancelled = false;
    const loader = new window.Image();
    const markReady = () => {
      if (!cancelled) setOverlayReady(true);
    };
    loader.onload = markReady;
    loader.onerror = markReady;
    loader.src = images[transition.index];
    return () => {
      cancelled = true;
    };
  }, [transition, images]);

  const resetDrag = () => {
    dragStartRef.current = null;
    dragDxRef.current = 0;
    setDragDx(0);
    setIsSnapping(false);
  };

  const snapBack = () => {
    dragStartRef.current = null;
    dragDxRef.current = 0;
    setDragDx(0);
    setIsSnapping(true);
    if (snapTimerRef.current !== null) {
      window.clearTimeout(snapTimerRef.current);
    }
    snapTimerRef.current = window.setTimeout(() => {
      setIsSnapping(false);
      snapTimerRef.current = null;
    }, SNAP_MS);
  };

  const beginTransition = (index: number) => {
    if (count <= 1 || transition) return;
    const target = ((index % count) + count) % count;
    if (target === currentIndex) return;

    // Pick the shortest path around the loop so the animation always
    // slides in from the correct side.
    let delta = target - currentIndex;
    if (delta > count / 2) delta -= count;
    else if (delta < -count / 2) delta += count;

    setOverlayReady(false);
    setTransition({ index: target, direction: delta > 0 ? 1 : -1 });
    resetDrag();
  };

  const goToPrevious = () => beginTransition(currentIndex - 1);
  const goToNext = () => beginTransition(currentIndex + 1);

  const finishTransition = () => {
    if (!transition) return;
    setCurrentIndex(transition.index);
    setOverlayReady(false);
    setTransition(null);
  };

  // ----- Swipe / drag support (mouse + touch via pointer events) -----
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (count <= 1 || transition) return;
    const targetEl = e.target as HTMLElement;
    // Ignore drags that start on the navigation buttons/dots.
    if (targetEl.closest('button')) return;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now(),
      pointerId: e.pointerId,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Pointer capture can throw for unsupported pointers – ignore.
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (!start || e.pointerId !== start.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;

    // Vertical intent: let the page/dialog scroll instead of swiping.
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 12) {
      dragStartRef.current = null;
      dragDxRef.current = 0;
      setDragDx(0);
      return;
    }

    dragDxRef.current = dx;
    setDragDx(dx);
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (!start || e.pointerId !== start.pointerId) return;

    const stageWidth = stageRef.current?.offsetWidth ?? 0;
    const dx = dragDxRef.current;
    const elapsed = Date.now() - start.time;
    const isFlick = Math.abs(dx) >= FLICK_MIN_DX && elapsed <= FLICK_MAX_MS;
    const threshold = stageWidth * SWIPE_FRACTION;

    if (dx <= -threshold || (isFlick && dx < 0)) {
      goToNext();
    } else if (dx >= threshold || (isFlick && dx > 0)) {
      goToPrevious();
    } else {
      snapBack();
    }
  };

  const handlePointerCancel = () => {
    resetDrag();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (count <= 1) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goToNext();
    }
  };

  if (count === 0) {
    return null;
  }

  const baseStyle =
    dragDx !== 0 || isSnapping ? { transform: `translateX(${dragDx}px)` } : undefined;

  return (
    <div className="image-carousel">
      <div
        ref={stageRef}
        className="carousel-container"
        role="group"
        aria-roledescription="carousel"
        aria-label={alt}
        tabIndex={count > 1 ? 0 : -1}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerCancel}
        onKeyDown={handleKeyDown}
      >
        <img
          className={`carousel-image carousel-base ${isSnapping ? 'carousel-snapping' : ''}`}
          style={baseStyle}
          src={images[currentIndex]}
          alt={`${alt} ${currentIndex + 1}`}
          draggable={false}
        />

        {transition && overlayReady && (
          <img
            key={transition.index}
            className={`carousel-image carousel-overlay ${
              transition.direction === 1
                ? 'carousel-slide-in-next'
                : 'carousel-slide-in-prev'
            }`}
            src={images[transition.index]}
            alt={`${alt} ${transition.index + 1}`}
            draggable={false}
            onAnimationEnd={finishTransition}
          />
        )}

        {count > 1 && (
          <>
            <IconButton
              className="carousel-button carousel-button-left"
              onClick={goToPrevious}
              size="small"
              aria-label="Previous image"
            >
              <NavigateBeforeIcon />
            </IconButton>

            <IconButton
              className="carousel-button carousel-button-right"
              onClick={goToNext}
              size="small"
              aria-label="Next image"
            >
              <NavigateNextIcon />
            </IconButton>

            <div className="carousel-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => beginTransition(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === currentIndex ? 'true' : 'false'}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="carousel-counter" aria-live="polite">
          {currentIndex + 1} / {count}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
